import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.paymentStatus === 'paid') {
        setStatus('success');
      } else if (attempts < 5) {
        // Poll again after 2 seconds
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < 5) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
          checkPaymentStatus();
        }, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-md"
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto mb-6 text-local-sage animate-spin" />
            <h1 className="font-manrope font-bold text-3xl mb-4">Verificando pago...</h1>
            <p className="text-muted-foreground">Por favor espera mientras konfirmamos tu kompra</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-local-sage/10 rounded-full flex items-center justify-center">
              <CheckCircle className="w-12 h-12 text-local-sage" />
            </div>
            <h1 className="font-manrope font-bold text-3xl mb-4">¡Kompra exitosa!</h1>
            <p className="text-muted-foreground mb-8">
              Tu kupon ha sido komprado exitosamente. El negocio ha recibido tu pedido y está preparándolo.
            </p>
            <div className="flex flex-col gap-4">
              <Button
                onClick={() => navigate('/profile')}
                className="bg-local-sage hover:bg-local-sage/90 rounded-full"
                data-testid="view-profile-button"
              >
                Ver mis kompras
              </Button>
              <Button
                onClick={() => navigate('/kupons')}
                variant="outline"
                className="rounded-full"
                data-testid="continue-shopping-button"
              >
                Kontinuar komprando
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 bg-destructive/10 rounded-full flex items-center justify-center">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="font-manrope font-bold text-3xl mb-4">Error al verificar</h1>
            <p className="text-muted-foreground mb-8">
              No pudimos verificar tu pago. Por favor kontakta con soporte o revisa tu perfil.
            </p>
            <Button
              onClick={() => navigate('/profile')}
              className="bg-local-sage hover:bg-local-sage/90 rounded-full"
            >
              Ir a mi perfil
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
};